/**
 * Tracking Active Banner
 * 
 * Displays when background location tracking is active during a trip
 */
import React from 'react';
import { Navigation, Radio, X } from 'lucide-react';

const TrackingActiveBanner = ({ 
  isVisible = false, 
  tripDuration,
  lastUpdate,
  queueSize = 0,
  onEndTrip,
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 bg-green-600 text-white px-4 py-2 shadow-lg"
      data-testid="tracking-active-banner"
    >
      <div className="flex items-center justify-between max-w-screen-xl mx-auto">
        <div className="flex items-center gap-3">
          {/* Animated pulse indicator */}
          <div className="relative">
            <Radio className="w-5 h-5" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-white rounded-full animate-pulse" />
          </div>
          
          <div className="flex flex-col">
            <span className="text-sm font-medium">Trip Tracking Active</span>
            <span className="text-xs text-green-100">
              {tripDuration && `${tripDuration} • `}
              {queueSize > 0 ? `${queueSize} queued` : 'Connected'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* GPS indicator */}
          <div className="flex items-center gap-1 text-xs bg-green-700 px-2 py-1 rounded-full">
            <Navigation className="w-3 h-3" />
            <span>GPS</span>
          </div>
          
          {/* End trip button */}
          {onEndTrip && (
            <button
              onClick={onEndTrip}
              className="p-1 hover:bg-green-700 rounded transition-colors"
              title="End Trip"
              data-testid="end-trip-banner-btn"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Tracking Active Pill
 * 
 * Smaller indicator for use in the map UI
 */
export const TrackingActivePill = ({ isVisible = false, source = 'fg' }) => {
  if (!isVisible) return null;

  return (
    <div 
      className="inline-flex items-center gap-1.5 bg-green-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-lg"
      data-testid="tracking-active-pill"
    >
      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
      <span>Tracking {source === 'bg' ? '(BG)' : ''}</span>
    </div>
  );
};

export default TrackingActiveBanner;
